/**
 * The PostHog destination adapter.
 *
 * PostHog receives every event (no name cap, no schema), so unlike `ga4.ts`
 * there is no allow-list and no parameter ceiling here. What this file owns is
 * the wire format for the `/batch/` endpoint, which lets the queue send one
 * request per flush instead of one `fetch` per event.
 *
 * Request shape, from the PostHog capture API docs:
 *
 *   POST <host>/batch/
 *   { "api_key": "<token>", "batch": [ { "event", "properties": { "distinct_id", ... }, "timestamp" } ] }
 *
 * `timestamp` is set from the queue's `enqueuedAt`, not from the moment of the
 * request, so a batched event keeps the time it actually happened.
 */

import type { QueuedAnalyticsEvent } from "./queue.ts";

/**
 * The anonymous visitor id the marketing site keys PostHog events by, kept in a
 * first-party cookie on `.tabbio.com` so app.tabbio.com reads the same id and
 * passes it to `$identify` as `$anon_distinct_id` after signup. That is what
 * joins a landing-page session to the person who later pays. Written only with
 * analytics consent, removed on withdrawal, same lifetime as `tabbio_attr`.
 */
export const POSTHOG_ANONYMOUS_ID_COOKIE = "tabbio_phid";
export const POSTHOG_ANONYMOUS_ID_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/** What the queue carries for a PostHog-bound event. */
export type PostHogQueuedPayload = {
  readonly distinctId: string;
  readonly properties: Record<string, unknown>;
};

export type PostHogBatchBody = {
  api_key: string;
  batch: Array<{
    event: string;
    properties: Record<string, unknown> & { distinct_id: string };
    timestamp: string;
  }>;
};

/** `https://us.i.posthog.com` → `https://us.i.posthog.com/batch/`. */
export function postHogBatchUrl(host: string): string {
  return `${host.replace(/\/+$/, "")}/batch/`;
}

/**
 * Builds the `/batch/` request body for a flushed batch.
 *
 * `distinct_id` is placed inside `properties`, which is where the batch
 * endpoint reads it (the single-event endpoint takes it at the top level; the
 * two differ). Properties are spread first so a stray `distinct_id` in event
 * properties cannot override the identity the queue captured at enqueue time.
 */
export function buildPostHogBatchBody(
  apiKey: string,
  batch: readonly QueuedAnalyticsEvent<PostHogQueuedPayload>[],
): PostHogBatchBody {
  return {
    api_key: apiKey,
    batch: batch.map((item) => ({
      event: item.name,
      properties: {
        ...item.payload.properties,
        distinct_id: item.payload.distinctId,
      },
      timestamp: new Date(item.enqueuedAt).toISOString(),
    })),
  };
}
